import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * AI 수정 제안 비교 모달
 * @param {object} props
 * @param {boolean} props.open
 * @param {{ original: string, modified: string }} props.data
 * @param {boolean} props.isProcessing
 * @param {() => void} props.onClose
 * @param {() => void} props.onApply
 */
export default function ArtifactDiffModal({
  open,
  data,
  isProcessing = false,
  onClose,
  onApply,
}) {
  if (!open) return null;

  const original = data?.original ?? "";
  const modified = data?.modified ?? "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      {/* 모달 내부에서만 Markdown 기본 스타일 복원 */}
      <style>{`
        .artifact-diff-markdown {
          color: #111827;
          line-height: 1.65;
          font-size: 14px;
        }
        .artifact-diff-markdown p { margin: 0.5rem 0; }
        .artifact-diff-markdown h1 { font-size: 1.25rem; font-weight: 900; margin: 0.9rem 0 0.5rem; }
        .artifact-diff-markdown h2 { font-size: 1.15rem; font-weight: 900; margin: 0.85rem 0 0.45rem; }
        .artifact-diff-markdown h3 { font-size: 1.05rem; font-weight: 900; margin: 0.75rem 0 0.4rem; }
        .artifact-diff-markdown h4, 
        .artifact-diff-markdown h5, 
        .artifact-diff-markdown h6 { font-size: 0.98rem; font-weight: 900; margin: 0.7rem 0 0.35rem; }
        .artifact-diff-markdown ul { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
        .artifact-diff-markdown ol { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
        .artifact-diff-markdown li { margin: 0.25rem 0; }
        .artifact-diff-markdown blockquote {
          margin: 0.6rem 0;
          padding: 0.6rem 0.75rem;
          border-left: 3px solid rgba(58, 102, 255, 0.45);
          background: rgba(58, 102, 255, 0.06);
          border-radius: 10px;
        }
        .artifact-diff-markdown code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.92em;
          background: rgba(17, 24, 39, 0.06);
          padding: 0.15rem 0.3rem;
          border-radius: 6px;
        }
        .artifact-diff-markdown pre {
          background: rgba(17, 24, 39, 0.06);
          padding: 0.75rem;
          border-radius: 12px;
          overflow: auto;
        }
        .artifact-diff-markdown pre code {
          background: transparent;
          padding: 0;
        }
        .artifact-diff-markdown table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.75rem 0;
        }
        .artifact-diff-markdown th,
        .artifact-diff-markdown td {
          border: 1px solid #e5e7eb;
          padding: 0.4rem 0.5rem;
          vertical-align: top;
        }
        .artifact-diff-markdown th {
          background: rgba(17, 24, 39, 0.04);
          font-weight: 800;
        }
        .artifact-diff-markdown a { color: #1d4ed8; text-decoration: underline; }
        .artifact-diff-original {
          text-decoration: line-through;
          opacity: 0.7;
        }
      `}</style>
      <div
        style={{
          width: "min(1240px, 98vw)",
          maxHeight: "85vh",
          background: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.3)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#ffffff",
          }}
        >
          <div style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
            AI 수정 제안
          </div>
          <button
            type="button"
            onClick={() => !isProcessing && onClose?.()}
            style={{
              border: "none",
              background: "transparent",
              cursor: isProcessing ? "not-allowed" : "pointer",
              fontSize: "22px",
              lineHeight: 1,
              color: "#6b7280",
              padding: "4px 8px",
              borderRadius: "10px",
            }}
            aria-label="닫기"
            disabled={isProcessing}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: "16px 20px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "14px",
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            background: "#f9fafb",
          }}
        >
          <div
            style={{
              border: "1px solid #fecaca",
              background: "#fef2f2",
              borderRadius: "14px",
              padding: "14px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#b91c1c", marginBottom: "8px" }}>
              변경 전
            </div>
            <div className="artifact-diff-markdown artifact-diff-original">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {original}
              </ReactMarkdown>
            </div>
          </div>

          <div
            style={{
              border: "1px solid #bbf7d0",
              background: "#f0fdf4",
              borderRadius: "14px",
              padding: "14px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#166534", marginBottom: "8px" }}>
              AI 제안
            </div>
            <div className="artifact-diff-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {modified}
              </ReactMarkdown>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "14px 20px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            background: "#ffffff",
          }}
        >
          <button
            type="button"
            onClick={() => !isProcessing && onClose?.()}
            disabled={isProcessing}
            style={{
              padding: "10px 14px",
              borderRadius: "12px",
              border: "1px solid #d1d5db",
              background: "#ffffff",
              color: "#374151",
              fontWeight: 600,
              cursor: isProcessing ? "not-allowed" : "pointer",
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => !isProcessing && onApply?.()}
            disabled={isProcessing}
            style={{
              padding: "10px 14px",
              borderRadius: "12px",
              border: "1px solid #3A66FF",
              background: "#3A66FF",
              color: "#ffffff",
              fontWeight: 700,
              cursor: isProcessing ? "not-allowed" : "pointer",
            }}
          >
            {isProcessing ? "적용 중..." : "적용하기"}
          </button>
        </div>
      </div>
    </div>
  );
}
